/**
 * 📝 Carga de Novedades para CMO
 *
 * Permite cargar novedades manualmente para empleados
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import {
  Delete,
  Search,
  Save
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const CargaNovedades = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estados del formulario
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [empleadosOptions, setEmpleadosOptions] = useState([]);
  const [busquedaEmpleado, setBusquedaEmpleado] = useState('');
  const [periodo, setPeriodo] = useState(new Date().toISOString().substr(0, 7)); // YYYY-MM
  const [centroCosto, setCentroCosto] = useState('');
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // Estados de datos
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [novedades, setNovedades] = useState([]);

  useEffect(() => {
    cargarCentrosCosto();
    cargarConceptos();
    cargarNovedades();
  }, [periodo]);

  useEffect(() => {
    if (busquedaEmpleado.length >= 2) {
      buscarEmpleados(busquedaEmpleado);
    } else {
      setEmpleadosOptions([]);
    }
  }, [busquedaEmpleado]);

  const cargarCentrosCosto = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/novedades/centros-costo`, {
        withCredentials: true
      });
      setCentrosCosto(response.data);
    } catch (error) {
      console.error('Error al cargar centros de costo:', error);
    }
  };

  const cargarConceptos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/novedades/conceptos`, {
        withCredentials: true
      });
      setConceptos(response.data);
    } catch (error) {
      console.error('Error al cargar conceptos:', error);
    }
  };

  const buscarEmpleados = async (query) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/novedades/empleados/buscar`, {
        params: { query },
        withCredentials: true
      });
      setEmpleadosOptions(response.data);
    } catch (error) {
      console.error('Error al buscar empleados:', error);
    }
  };

  const cargarNovedades = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/novedades`, {
        params: { periodo },
        withCredentials: true
      });
      setNovedades(response.data);
    } catch (error) {
      console.error('Error al cargar novedades:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar novedades'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarNovedad = async () => {
    if (!empleadoSeleccionado || !concepto || !centroCosto || !monto) {
      setMessage({ type: 'warning', text: 'Complete todos los campos requeridos' });
      return;
    }

    try {
      setLoading(true);

      const conceptoObj = conceptos.find(c => c.codigo === concepto);

      await axios.post(
        `${API_BASE_URL}/api/novedades`,
        {
          legajo: empleadoSeleccionado.legajo,
          empleado_nombre: empleadoSeleccionado.nombre,
          periodo,
          concepto: conceptoObj ? conceptoObj.nombre : concepto,
          centro_costo: centroCosto,
          monto: parseFloat(monto),
          observaciones
        },
        { withCredentials: true }
      );

      setMessage({ type: 'success', text: 'Novedad guardada correctamente' });

      // Limpiar formulario
      setEmpleadoSeleccionado(null);
      setBusquedaEmpleado('');
      setConcepto('');
      setCentroCosto('');
      setMonto('');
      setObservaciones('');

      // Recargar novedades
      cargarNovedades();

    } catch (error) {
      console.error('Error al guardar novedad:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar novedad'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarNovedad = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta novedad?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/novedades/${id}`, {
        withCredentials: true
      });

      setMessage({ type: 'success', text: 'Novedad eliminada correctamente' });
      cargarNovedades();

    } catch (error) {
      console.error('Error al eliminar novedad:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al eliminar novedad'
      });
    }
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor || 0);
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {/* Formulario de carga */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Nueva Novedad
          </Typography>

          {/* Fila 1: campos de datos */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ flex: '0 0 160px' }}>
              <TextField
                fullWidth
                label="Período (YYYY-MM)"
                type="month"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ flex: 2, minWidth: 220 }}>
              <Autocomplete
                options={empleadosOptions}
                getOptionLabel={(option) => `${option.legajo} - ${option.nombre}`}
                value={empleadoSeleccionado}
                onChange={(event, newValue) => setEmpleadoSeleccionado(newValue)}
                inputValue={busquedaEmpleado}
                onInputChange={(event, newInputValue) => setBusquedaEmpleado(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Empleado"
                    placeholder="Legajo o apellido..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ mr: 1, color: 'action.active' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">
                        <strong>{option.legajo}</strong> - {option.nombre}
                      </Typography>
                      {option.jornada && (
                        <Typography variant="caption" color="text.secondary">
                          Jornada: {option.jornada} | Categoría: {option.categoria || 'N/A'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ flex: 2, minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Centro de Costo</InputLabel>
                <Select
                  value={centroCosto}
                  onChange={(e) => setCentroCosto(e.target.value)}
                  label="Centro de Costo"
                >
                  {centrosCosto.map((c) => (
                    <MenuItem key={c.codigo} value={c.codigo}>
                      {c.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 2, minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Concepto</InputLabel>
                <Select
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  label="Concepto"
                >
                  {conceptos.map((c) => (
                    <MenuItem key={c.codigo} value={c.codigo}>
                      {c.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: 130 }}>
              <TextField
                fullWidth
                label="Valor"
                type="number"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
          </Box>

          {/* Fila 2: observaciones + botón */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '0 0 180px' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Save />}
                onClick={handleGuardarNovedad}
                disabled={loading || !empleadoSeleccionado || !concepto || !centroCosto || !monto}
                sx={{ height: '64px' }}
              >
                Guardar Novedad
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Listado de novedades */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Novedades del período {periodo}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : novedades.length === 0 ? (
            <Alert severity="info">No hay novedades cargadas para este período</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Legajo</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Empleado</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Centro de Costo</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Observaciones</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {novedades.map((nov) => (
                    <TableRow key={nov.id} hover>
                      <TableCell>{nov.legajo}</TableCell>
                      <TableCell>{nov.empleado_nombre}</TableCell>
                      <TableCell>{nov.centro_costo || '-'}</TableCell>
                      <TableCell>{nov.concepto}</TableCell>
                      <TableCell align="right"><strong>{formatearMoneda(nov.monto)}</strong></TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {nov.observaciones || '-'}
                      </TableCell>
                      <TableCell>{nov.usuario_nombre || nov.usuario_carga}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleEliminarNovedad(nov.id)}
                          title="Eliminar"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CargaNovedades;
